export function metresToMiles(metres: number) {
  return (metres / 1609.344).toFixed(1);
}

export function metresToKilometres(metres: number) {
  return (metres / 1000).toFixed(1);
}

export function generateDistanceLabel(
  metres: number,
  radiusUnit: string,
  milesTranslated: string,
  kmTranslated: string
) {
  if (!metres) return '';
  if (radiusUnit === 'km') {
    return `${metresToKilometres(metres)} ${kmTranslated}`;
  }

  return `${metresToMiles(metres)} ${milesTranslated}`;
}

// Countries whose users expect miles for distance. Explicit by design — extend as product decides.
const IMPERIAL_COUNTRIES = new Set(['US']);

export type RadiusUnit = 'km' | 'miles';

// Region subtag of a BCP-47 locale, e.g. "en-us" -> "US", "fr-ca" -> "CA", "de" -> undefined.
function regionFromLocale(locale?: string): string | undefined {
  const parts = locale?.split('-') ?? [];

  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : undefined;
}

// Country-based default distance unit; falls back to km for any non-imperial or region-less locale.
export function getDefaultRadiusUnit(locale?: string): RadiusUnit {
  const region = regionFromLocale(locale);

  return region && IMPERIAL_COUNTRIES.has(region) ? 'miles' : 'km';
}

export function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  // Haversine formula to calculate distance between two points on a sphere
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180; // Convert degrees to radians
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

interface LatLng {
  lat: () => number;
  lng: () => number;
}

export function computeOffset(from: LatLng, distance: number, heading: number): LatLng {
  const EarthRadius = 6371000; // Earth's radius in meters
  const lat1 = (from.lat() * Math.PI) / 180;
  const lng1 = (from.lng() * Math.PI) / 180;
  const headingRad = (heading * Math.PI) / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distance / EarthRadius) +
      Math.cos(lat1) * Math.sin(distance / EarthRadius) * Math.cos(headingRad)
  );

  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(headingRad) * Math.sin(distance / EarthRadius) * Math.cos(lat1),
      Math.cos(distance / EarthRadius) - Math.sin(lat1) * Math.sin(lat2)
    );

  return {
    lat: () => lat2 * (180 / Math.PI),
    lng: () => lng2 * (180 / Math.PI),
  };
}

// Function to calculate bounds with radius
export function calculateBoundsWithRadius(centerLat: number, centerLng: number, radius: number) {
  const latChange = (radius / 6371e3) * (180 / Math.PI); // Latitude change in degrees
  const lngChange = ((radius / 6371e3) * (180 / Math.PI)) / Math.cos((centerLat * Math.PI) / 180); // Longitude change in degrees

  const newSWCorner = {
    lat: centerLat - latChange,
    lng: centerLng - lngChange,
  };
  const newNECorner = {
    lat: centerLat + latChange,
    lng: centerLng + lngChange,
  };

  return new google.maps.LatLngBounds(newSWCorner, newNECorner);
}
